// Sugalarm — subscription wrappers (cycle 464).
//
// Reads the server's answer and nothing else: the client never decides
// entitlement. `GET /api/me/subscription` accepts a device token (a kiosk may
// SHOW status); the three writes are JWT-only on the server, so a kiosk's
// device token is refused even if a control were somehow reached.

import { request } from "../lib/apiClient";
import type { ApiResult } from "../lib/types";
import type { SubscriptionStateName } from "../lib/billing";

/** The cycle-462 `GET /api/me/subscription` contract. No provider ids. */
export interface Subscription {
  state: SubscriptionStateName;
  source: "apple" | "google" | "stripe" | "comp" | null;
  trial_eligible: boolean;
  trial_ends_at: string | null;
  renews_at: string | null;
  grace_ends_at: string | null;
  enforced: boolean;
  manage: "apple" | "google" | "stripe" | null;
  price_display: string;
  trial_days: number;
  grace_days: number;
  /** Cycle 468: the lock as it STANDS (never derived from the clock) — the
   *  same two words the WS `service_state` frame carries — and whether this
   *  account is exempt from the lock. Optional so a pre-468 server's answer
   *  still types; absent reads as `active`. */
  locked_at?: string | null;
  service_state?: "active" | "locked";
  exempt?: boolean;
}

export function getMySubscription(
  opts: { signal?: AbortSignal } = {},
): Promise<ApiResult<Subscription>> {
  return request<Subscription>("/api/me/subscription", { signal: opts.signal });
}

/** Stamp that this ACCOUNT has been shown the free-month step (server-side,
 *  `users.settings.subscription_step_done_at`). Idempotent. */
export function markSubscriptionStepDone(): Promise<
  ApiResult<{ done: boolean; done_at: string }>
> {
  return request<{ done: boolean; done_at: string }>(
    "/api/me/subscription/onboarding-done",
    { method: "POST", body: {} },
  );
}

/** Cycle 463: a one-time handoff URL to thesugaralarm.com/subscribe.html
 *  (the token rides in the URL fragment). */
export function mintWebHandoff(): Promise<ApiResult<{ url: string }>> {
  return request<{ url: string }>("/api/billing/web/handoff", {
    method: "POST",
    body: {},
  });
}

/** Cycle 463: the Stripe Customer Portal for the signed-in account. */
export function openStripePortal(): Promise<ApiResult<{ portal_url: string }>> {
  return request<{ portal_url: string }>("/api/billing/stripe/portal", {
    method: "POST",
    body: {},
  });
}

/** Cycle 465: which Apple product to show (the trial product only while the
 *  account never used a trial on any rail) and the account's OPAQUE purchase
 *  token, the StoreKit `appAccountToken`. Minted once per account on the
 *  server, never the account id. JWT only. */
export interface AppleOffer {
  product_id: string;
  has_trial: boolean;
  app_account_token: string;
}

export function getAppleOffer(): Promise<ApiResult<AppleOffer>> {
  return request<AppleOffer>("/api/billing/apple/offer");
}

/** Cycle 465: one signed StoreKit transaction (JWS) → the server's own
 *  `GET /api/me/subscription` answer once it verified the transaction, bound
 *  it to this account and re-checked it with the App Store Server API. A
 *  403 `account_mismatch` is another account's purchase. JWT only. */
export function verifyAppleTransaction(
  signedTransaction: string,
): Promise<ApiResult<Subscription>> {
  return request<Subscription>("/api/billing/apple/verify", {
    method: "POST",
    body: { signed_transaction: signedTransaction },
  });
}

/** Cycle 466: which Play product, base plan and offer to show (the
 *  free-month offer only while the account never used a trial on any
 *  rail; `offer_id` is null otherwise) and the account's OPAQUE purchase
 *  token, which the Android app sets as the purchase's obfuscated account
 *  id. The same token Apple gets; never the account id. JWT only. */
export interface GoogleOffer {
  product_id: string;
  base_plan_id: string;
  offer_id: string | null;
  obfuscated_account_id: string;
}

export function getGoogleOffer(): Promise<ApiResult<GoogleOffer>> {
  return request<GoogleOffer>("/api/billing/google/offer");
}

/** Cycle 466: one Play purchase token → the server's own
 *  `GET /api/me/subscription` answer once it re-read the purchase from the
 *  Android Publisher API, bound it to this account, wrote it and
 *  acknowledged it. A 403 `account_mismatch` is another account's
 *  purchase. JWT only. */
export function verifyGooglePurchase(
  purchaseToken: string,
  productId: string,
): Promise<ApiResult<Subscription>> {
  return request<Subscription>("/api/billing/google/verify", {
    method: "POST",
    body: { purchase_token: purchaseToken, product_id: productId },
  });
}
